import Navbar from './components/Navbar';
import Hero from './components/Hero';
import CategorySection from './components/CategorySection';
import StatsSection from './components/StatsSection';
import ArticleSection from './components/ArticleSection';
import TestimonialSection from './components/TestimonialSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import { generateWebSiteSchema, generateOrganizationSchema } from '@/utils/seo';

export default function Home() {
  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(generateWebSiteSchema()) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(generateOrganizationSchema()) }}
      />
      <Navbar />
      <Hero />
      <CategorySection />
      <StatsSection />
      <article>
        <section className="py-20 md:py-24 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-primary-500 mb-3 block">
                About Superbuy Sheet
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                What Is <span className="italic text-primary-500">Superbuy Sheet</span> and Why You Need It
              </h2>
            </div>

            <div className="prose prose-lg max-w-none text-foreground-600 leading-relaxed space-y-6">
              <p className="text-base md:text-lg">
                A <strong>Superbuy Sheet</strong> is more than just a product list — it is a carefully curated, regularly updated database that connects shoppers directly to verified products from trusted sellers across multiple platforms. Whether you are hunting for the latest sneaker releases, premium streetwear, luxury accessories, or everyday essentials, a well-organized <strong>Superbuy spreadsheet</strong> saves you hours of manual searching and provides the confidence that comes with verified product data.
              </p>

              <p className="text-base md:text-lg">
                The concept behind the <strong>Superbuy Sheet</strong> is simple yet powerful: instead of scrolling through endless product pages, you access a structured spreadsheet that contains direct product links, QC (Quality Control) photos, seller ratings, pricing information, and availability data — all in one place. This approach transforms the shopping experience from a time-consuming treasure hunt into an efficient, data-driven process.
              </p>

              <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">
                Why Superbuy Sheet Matters for Smart Shoppers
              </h3>

              <p className="text-base md:text-lg">
                In the world of international shopping, having access to reliable <strong>Superbuy spreadsheet</strong> resources can make the difference between a successful purchase and a disappointing experience. The spreadsheet format allows for easy filtering, sorting, and comparison — features that traditional product browsing simply cannot match. With a <strong>Superbuy Sheet</strong>, you can quickly identify trending products, compare prices across sellers, and check QC photos before committing to a purchase.
              </p>

              <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">
                Key Benefits of Using Superbuy Sheet
              </h3>

              <ul className="list-disc pl-6 space-y-3 text-base md:text-lg">
                <li>
                  <strong>Time Efficiency:</strong> A comprehensive <strong>Superbuy spreadsheet</strong> eliminates the need to manually search through multiple platforms. All relevant product information is organized and accessible in one place.
                </li>
                <li>
                  <strong>Verified Data:</strong> Every entry in a quality <strong>Superbuy Sheet</strong> includes seller verification, QC photos, and updated pricing, reducing the risk of purchasing from unreliable sources.
                </li>
                <li>
                  <strong>Category Organization:</strong> Whether you are looking for shoes, hoodies, jackets, bags, or accessories, the <strong>Superbuy spreadsheet</strong> format makes category navigation intuitive and fast.
                </li>
                <li>
                  <strong>Regular Updates:</strong> The best <strong>Superbuy Sheet</strong> resources are updated weekly with new product finds, discontinued items removed, and fresh seller data added.
                </li>
                <li>
                  <strong>Shipping Optimization:</strong> Many <strong>Superbuy spreadsheet</strong> guides include shipping weight estimates and optimization tips, helping you reduce international shipping costs significantly.
                </li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">
                How to Get Started with Superbuy Sheet
              </h3>

              <p className="text-base md:text-lg">
                Getting started with a <strong>Superbuy Sheet</strong> is straightforward. Browse the categories above to find the product type you are interested in — whether that is shoes, hoodies, t-shirts, jackets, pants, headwear, sets, underwear, jerseys, accessories, bags, belts, perfumes, or other items. Each category link takes you to a curated <strong>Superbuy spreadsheet</strong> collection with detailed product information, seller data, and QC resources. For a complete walkthrough, check out our in-depth <strong>Superbuy Sheet</strong> guide linked below.
              </p>
            </div>

            <div className="mt-10 text-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-8 py-4 rounded-full font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                Explore Superbuy Sheet Now
                <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
              </a>
            </div>
          </div>
        </section>
      </article>
      <ArticleSection />
      <TestimonialSection />
      <CTASection />
      <Footer />
    </main>
  );
}